import Box from '@mui/material/Box';
import { type ReactNode, useEffect, useRef } from 'react';

import { getFieldDef } from '@/core/dcr/fields/registry';
import type { FieldDef } from '@/core/dcr/fields/types';

import { useDcrFormContext } from '../_context/DcrFormContext';
import { CollectionSection } from '../Collections/CollectionSection';

import {
	FLASH_KEYFRAMES,
	FLASH_TIMING,
	dcrFieldStyles as sx
} from './DcrField.styles';
import { EmailListInput } from './renderers/EmailListInput';
import { MultiPicklistInput } from './renderers/MultiPicklistInput';
import { PicklistInput } from './renderers/PicklistInput';
import { TextInput } from './renderers/TextInput';
import { YearInput } from './renderers/YearInput';

export const fieldElementId = (name: string) =>
	`dcr-field-${name.replace(/\./g, '-')}`;

type FrameProps = {
	name: string;
	canFlash: boolean;
	children: ReactNode;
};

export const flashElement = (element: HTMLElement | null) => {
	if (!element) return;

	element.animate?.(FLASH_KEYFRAMES, FLASH_TIMING);
	element.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
	element
		.querySelector<HTMLElement>('input, textarea, [contenteditable="true"]')
		?.focus({ preventScroll: true });
};

const FieldFrame = ({ name, canFlash, children }: FrameProps) => {
	const { disclosure } = useDcrFormContext();
	const ref = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (canFlash && disclosure.takeFlash(name)) flashElement(ref.current);
	}, [canFlash, disclosure, name]);

	return (
		<Box
			ref={ref}
			id={fieldElementId(name)}
			sx={canFlash ? sx.flashable : undefined}
		>
			{children}
		</Box>
	);
};

const FieldRenderer = ({ name, def }: { name: string; def: FieldDef }) => {
	switch (def.kind) {
		case 'text':
		case 'url':
		case 'phone':
		case 'date':
			return <TextInput name={name} def={def} />;
		case 'email':
			return <EmailListInput name={name} def={def} />;
		case 'picklist':
			return <PicklistInput name={name} def={def} />;
		case 'year':
			return <YearInput name={name} def={def} />;
		case 'multiPicklist':
			return <MultiPicklistInput name={name} def={def} />;
		case 'collection':
			return <CollectionSection name={name} def={def} />;
		default:
			return null;
	}
};

export const RENDERED_KINDS: readonly FieldDef['kind'][] = [
	'text',
	'url',
	'phone',
	'date',
	'email',
	'picklist',
	'year',
	'multiPicklist',
	'collection'
];

type Props = {
	name: string;
};

export const DcrField = ({ name }: Props) => {
	const { entityType, isAvailable, disclosure } = useDcrFormContext();
	const def = getFieldDef(entityType, name);

	if (!def || def.groupWith) return null;

	const isVisible = def.visibility === 'always' || disclosure.isAdded(name);
	if (!isVisible || !isAvailable(name, def)) return null;

	return (
		<FieldFrame
			name={name}
			canFlash={def.visibility === 'addable' && def.kind !== 'collection'}
		>
			<FieldRenderer name={name} def={def} />
		</FieldFrame>
	);
};
